import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useAuthStore } from "@/store/authStore";
import { useConsentStore } from "@/store/consentStore";
import { consentApi } from "@/lib/api";
import AppShell from "@/components/AppShell";
import ProtectedRoute from "@/components/ProtectedRoute";
import FeatureConsentGate from "@/components/FeatureConsentGate";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import LanguageSelect from "@/pages/LanguageSelect";
import Home from "@/pages/Home";
import ForYou from "@/pages/ForYou";
import FindHospitals from "@/pages/FindHospitals";
import FindPharmacies from "@/pages/FindPharmacies";
import BuyMedicine from "@/pages/BuyMedicine";
import OrderMedicines from "@/pages/OrderMedicines";
import PharmacyChat from "@/pages/PharmacyChat";
import PharmacyInbox from "@/pages/PharmacyInbox";
import PlaceOrder from "@/pages/PlaceOrder";
import HospitalDetail from "@/pages/HospitalDetail";
import DoctorListing from "@/pages/DoctorListing";
import DrSanjeevani from "@/pages/DrSanjeevani";
import HealthRecords from "@/pages/HealthRecords";
import AbhaLink from "@/pages/AbhaLink";
import ShareAbha from "@/pages/ShareAbha";
import FetchAbha from "@/pages/FetchAbha";
import CareHub from "@/pages/CareHub";
import More from "@/pages/More";
import Profile from "@/pages/Profile";
import EmergencySOS from "@/pages/EmergencySOS";
import ConsentSettings from "@/pages/ConsentSettings";
import TrataAI from "@/pages/TrataAI";
import ServicePage from "@/pages/ServicePage";
import Consultations from "@/pages/Consultations";
import BookingsReports from "@/pages/BookingsReports";
import Notifications from "@/pages/Notifications";
import DailyTracker from "@/pages/DailyTracker";
import EmergencyContacts from "@/pages/EmergencyContacts";
import DrugInteraction from "@/pages/DrugInteraction";
import SecondOpinion from "@/pages/SecondOpinion";
import FindLabs from "@/pages/FindLabs";
import Rewards from "@/pages/Rewards";
import AccountSettings from "@/pages/AccountSettings";
import AiDoctor from "@/pages/AiDoctor";
import ScreeningHistory from "@/pages/ScreeningHistory";
import AtHomeMyBookings from "@/pages/AtHomeMyBookings";
import AmbulancePage from "@/pages/Ambulance";
import CaseStatus from "@/pages/CaseStatus";
import VitalMap from "@/pages/VitalMap";
import LabBooking from "@/pages/LabBooking";
import DiagnosticCentres from "@/pages/DiagnosticCentres";
import ReferralLanding from "@/pages/ReferralLanding";
import SeniorCare from "@/pages/SeniorCare";
import SeniorCareGuardian from "@/pages/SeniorCareGuardian";
import SeniorCareDashboard from "@/pages/SeniorCareDashboard";
import SeniorCareServices from "@/pages/SeniorCareServices";
import SeniorCareBookNurse from "@/pages/SeniorCareBookNurse";
import SpecialistCare from "@/pages/SpecialistCare";
import WomenCare from "@/pages/WomenCare";
import CancerCare from "@/pages/CancerCare";
import DiabetesReversal from "@/pages/DiabetesReversal";
import Medications from "@/pages/Medications";
import VideoConsult from "@/pages/VideoConsult";
import MentalWellness from "@/pages/MentalWellness";
import WellnessConsultants from "@/pages/WellnessConsultants";
import WellnessTherapist from "@/pages/WellnessTherapist";
import WellnessBook from "@/pages/WellnessBook";
import WellnessSessions from "@/pages/WellnessSessions";
import AdminChoice from "@/pages/AdminChoice";
import AdminPortal from "@/pages/AdminPortal";
import { initAnalytics, setAnalyticsUser, trackNavigation } from "@/lib/analytics";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AnalyticsTracker() {
  const { pathname } = useLocation();
  useEffect(() => { try { trackNavigation(pathname); } catch (e) { /* ignore */ } }, [pathname]);
  return null;
}

function App() {
  const user = useAuthStore((s) => s.user);
  const hydrateFromServer = useConsentStore((s) => s.hydrateFromServer);

  useEffect(() => { initAnalytics(); }, []);
  useEffect(() => {
    setAnalyticsUser({ userId: user?._id || user?.id || null, phone: user?.phone || null });
  }, [user?._id, user?.id, user?.phone]);

  useEffect(() => {
    if (!user?.phone) return;
    consentApi
      .get(user.phone)
      .then((res) => hydrateFromServer(res.data?.consents))
      .catch(() => hydrateFromServer(null));
  }, [user?.phone, hydrateFromServer]);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <AnalyticsTracker />
      <FeatureConsentGate />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/language-select" element={<LanguageSelect />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/r/:code" element={<ReferralLanding />} />
        <Route path="/admin-choice" element={<ProtectedRoute><AdminChoice /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPortal /></ProtectedRoute>} />
        <Route
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route path="/home" element={<Home />} />
          <Route path="/for-you" element={<ForYou />} />
          <Route path="/trata-ai" element={<TrataAI />} />
          <Route path="/care-hub" element={<CareHub />} />
          <Route path="/more" element={<More />} />
          <Route path="/find-care" element={<FindHospitals />} />
          <Route path="/find-pharmacies" element={<FindPharmacies />} />
          <Route path="/buy-medicine" element={<BuyMedicine />} />
          <Route path="/order-medicines" element={<OrderMedicines />} />
          <Route path="/pharmacy-chat" element={<PharmacyChat />} />
          <Route path="/pharmacy-inbox" element={<PharmacyInbox />} />
          <Route path="/place-order" element={<PlaceOrder />} />
          <Route path="/hospital/:id" element={<HospitalDetail />} />
          <Route path="/doctors" element={<DoctorListing />} />
          <Route path="/assistant" element={<DrSanjeevani />} />
          <Route path="/records" element={<HealthRecords />} />
          <Route path="/abha" element={<AbhaLink />} />
          <Route path="/abha/share" element={<ShareAbha />} />
          <Route path="/abha/fetch" element={<FetchAbha />} />
          <Route path="/vital-map" element={<VitalMap />} />
          <Route path="/lab-booking" element={<LabBooking />} />
          <Route path="/diagnostic-centres" element={<DiagnosticCentres />} />
          {/* Senior Care (PeakAge-style orchestrator) */}
          <Route path="/senior-care" element={<SeniorCare />} />
          <Route path="/senior-care/guardian" element={<SeniorCareGuardian />} />
          <Route path="/senior-care/dashboard" element={<SeniorCareDashboard />} />
          <Route path="/senior-care/services" element={<SeniorCareServices />} />
          <Route path="/senior-care/book-nurse" element={<SeniorCareBookNurse />} />
          <Route path="/specialist-care" element={<SpecialistCare />} />
          <Route path="/women-care" element={<WomenCare />} />
          <Route path="/cancer-care" element={<CancerCare />} />
          <Route path="/diabetes-reversal" element={<DiabetesReversal />} />
          <Route path="/medications" element={<Medications />} />
          {/* Mental Wellness */}
          <Route path="/mental-wellness" element={<MentalWellness />} />
          <Route path="/mental-wellness/consultants" element={<WellnessConsultants />} />
          <Route path="/mental-wellness/therapist/:id" element={<WellnessTherapist />} />
          <Route path="/mental-wellness/book/:id" element={<WellnessBook />} />
          <Route path="/mental-wellness/my-sessions" element={<WellnessSessions />} />
          <Route path="/prescriptions" element={<Medications />} />
          <Route path="/emergency" element={<EmergencySOS />} />
          <Route path="/consent" element={<ConsentSettings />} />
          <Route path="/profile" element={<Profile />} />
          {/* Menu-linked screens */}
          <Route path="/ambulance" element={<AmbulancePage />} />
          <Route path="/case-status/:id" element={<CaseStatus />} />
          <Route path="/paramedic" element={<ServicePage slug="paramedic" />} />
          <Route path="/emergency-contacts" element={<EmergencyContacts />} />
          <Route path="/health-center" element={<ServicePage slug="health-center" />} />
          <Route path="/smartwatch" element={<ServicePage slug="smartwatch" />} />
          <Route path="/daily-tracker" element={<DailyTracker />} />
          <Route path="/health-companion" element={<ServicePage slug="health-companion" />} />
          <Route path="/at-home-care/lab-tests" element={<ServicePage slug="at-home-care/lab-tests" />} />
          <Route path="/at-home-care/teleconsultation" element={<ServicePage slug="at-home-care/teleconsultation" />} />
          <Route path="/at-home-care/nursing" element={<ServicePage slug="at-home-care/nursing" />} />
          <Route path="/at-home-care/physiotherapy" element={<ServicePage slug="at-home-care/physiotherapy" />} />
          <Route path="/at-home-care/advanced-care" element={<ServicePage slug="at-home-care/advanced-care" />} />
          <Route path="/at-home-care/therapy" element={<ServicePage slug="at-home-care/therapy" />} />
          <Route path="/at-home-care/my-bookings" element={<AtHomeMyBookings />} />
          <Route path="/consultations" element={<Consultations />} />
          <Route path="/consult/:bookingId/call" element={<VideoConsult />} />
          <Route path="/drug-interaction" element={<DrugInteraction />} />
          <Route path="/second-opinion" element={<SecondOpinion />} />
          <Route path="/ai-doctor" element={<AiDoctor />} />
          <Route path="/ai-doctor/history" element={<ScreeningHistory />} />
          <Route path="/find-labs" element={<FindLabs />} />
          <Route path="/bookings-reports" element={<BookingsReports />} />
          <Route path="/mom-baby" element={<ServicePage slug="mom-baby" />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/account-settings" element={<AccountSettings />} />
          <Route path="/rewards" element={<Rewards />} />
          <Route path="/support" element={<ServicePage slug="support" />} />
          <Route path="/app-guide" element={<ServicePage slug="app-guide" />} />
          <Route path="/how-it-works" element={<ServicePage slug="how-it-works" />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster position="top-center" richColors />
    </BrowserRouter>
  );
}

export default App;
